import React, { useState } from 'react';
import type { Game, Round } from '@aco/shared';
import { getPprPlayers } from '../utils/game-utils.js';

interface RoundSelectionDialogProps {
  completedRounds: Round[];
  game: Game;
  onSelect: (round: Round) => void;
  onCancel: () => void;
}

/**
 * Full-size dialog for selecting a round to correct.
 * 80dvh × 90vw, centered with dark backdrop — matches the confirmation dialog
 * and score entry overlays in style. Uses containerType:'size' for cqh/cqw
 * internal sizing. Shows a scrollable list of completed rounds; user selects
 * one before proceeding via "Edit Round X".
 */
export function RoundSelectionDialog({
  completedRounds,
  game,
  onSelect,
  onCancel,
}: RoundSelectionDialogProps) {
  const [selectedRound, setSelectedRound] = useState<Round | null>(null);

  const backdropStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.72)',
    fontFamily: "'Inter', sans-serif",
  };

  const dialogStyle: React.CSSProperties = {
    height: '80dvh',
    width: '90vw',
    background: '#1a1a2e',
    borderRadius: '20px',
    border: '1px solid rgba(255,255,255,0.14)',
    boxShadow: '0 12px 48px rgba(0,0,0,0.6)',
    color: '#fff',
    fontFamily: "'Inter', sans-serif",
    containerType: 'size',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const isSelected = (round: Round) =>
    selectedRound?.roundNumber === round.roundNumber;

  return (
    <div style={backdropStyle} role="dialog" aria-modal="true" aria-label="Select round to correct">
      <div style={dialogStyle}>
        {/* Header */}
        <div
          style={{
            padding: '4cqh 5cqw 3cqh',
            flexShrink: 0,
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '5cqh',
              fontWeight: 800,
              fontFamily: "'Inter', sans-serif",
              textAlign: 'center',
            }}
          >
            Select Round to Correct
          </h2>
        </div>

        {/* Scrollable round list */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y',
            padding: '1cqh 3cqw',
            display: 'flex',
            flexDirection: 'column',
            gap: '1cqh',
          }}
        >
          {completedRounds.map((round) => {
            const selected = isSelected(round);
            // Compute the correct A/B player names for this specific round so
            // doubles rows show the actual thrower (odd → A, even → B).
            const rowPlayers = getPprPlayers(game, 'team1', round.roundNumber);
            const rowTeam1Name = rowPlayers[0].name;
            const rowTeam2Name = rowPlayers[1].name;
            return (
              <button
                key={round.roundNumber}
                onClick={() => setSelectedRound(round)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3cqw',
                  padding: '0 3cqw',
                  minHeight: 'max(96px, 8cqh)',
                  width: '100%',
                  textAlign: 'left',
                  border: selected
                    ? '2px solid #2563eb'
                    : '2px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  background: selected
                    ? 'rgba(37,99,235,0.22)'
                    : 'rgba(255,255,255,0.04)',
                  color: '#fff',
                  cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif",
                  touchAction: 'manipulation',
                  transition: 'background 0.12s, border-color 0.12s',
                  flexShrink: 0,
                }}
                aria-pressed={selected}
              >
                {/* Round number badge */}
                <span
                  style={{
                    minWidth: '6cqw',
                    fontSize: '5.6cqh',
                    fontWeight: 700,
                    opacity: selected ? 1 : 0.5,
                    fontFamily: "'Inter', sans-serif",
                    textAlign: 'center',
                  }}
                >
                  R{round.roundNumber}
                </span>

                {/* Team 1 score */}
                <span style={{ flex: 1, fontSize: '5.6cqh', fontFamily: "'Inter', sans-serif" }}>
                  {rowTeam1Name}:{' '}
                  <strong style={{ fontSize: '6.4cqh' }}>{round.team1Score}</strong>
                </span>

                {/* Divider */}
                <span style={{ opacity: 0.3, fontSize: '5cqh', fontFamily: "'Inter', sans-serif" }}>
                  |
                </span>

                {/* Team 2 score */}
                <span style={{ flex: 1, fontSize: '5.6cqh', fontFamily: "'Inter', sans-serif" }}>
                  {rowTeam2Name}:{' '}
                  <strong style={{ fontSize: '6.4cqh' }}>{round.team2Score}</strong>
                </span>
              </button>
            );
          })}
        </div>

        {/* Action buttons */}
        <div
          style={{
            display: 'flex',
            gap: '3cqw',
            padding: '3cqh 4cqw',
            flexShrink: 0,
            borderTop: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              height: '10cqh',
              minHeight: '56px',
              borderRadius: '12px',
              border: '1.5px solid rgba(255,255,255,0.25)',
              background: 'transparent',
              color: '#fff',
              fontWeight: 600,
              fontSize: '3.5cqh',
              fontFamily: "'Inter', sans-serif",
              cursor: 'pointer',
              touchAction: 'manipulation',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => selectedRound && onSelect(selectedRound)}
            disabled={!selectedRound}
            style={{
              flex: 2,
              height: '10cqh',
              minHeight: '56px',
              borderRadius: '12px',
              border: 'none',
              background: selectedRound ? '#2563eb' : 'rgba(255,255,255,0.12)',
              color: selectedRound ? '#fff' : 'rgba(255,255,255,0.35)',
              fontWeight: 700,
              fontSize: '3.5cqh',
              fontFamily: "'Inter', sans-serif",
              cursor: selectedRound ? 'pointer' : 'not-allowed',
              touchAction: 'manipulation',
              transition: 'background 0.15s, color 0.15s',
            }}
            aria-disabled={!selectedRound}
          >
            {selectedRound ? `Edit Round ${selectedRound.roundNumber}` : 'Edit Round'}
          </button>
        </div>
      </div>
    </div>
  );
}
